import React, {useEffect, useRef, useState} from "react"
import {Button, Icon, Input, Grid, Modal} from "semantic-ui-react"
import {ScreenSize} from "./utils/media-utils"
import {FormattedMessage, useIntl} from "react-intl"
import {createCheckoutSession, validateKey} from "./api/api"
import {useLocation} from "react-router"
import * as Sentry from "@sentry/react"


type PricingModalProps = {
    screenSize: ScreenSize
    open: boolean
    onClose: () => void
    checkoutSuccess: boolean
    setCheckoutSuccess: (open: boolean) => void
    setContactModalOpen: (open: boolean) => void
}

export function PricingModal({ screenSize, open, onClose, checkoutSuccess, setCheckoutSuccess, setContactModalOpen }: PricingModalProps) {
    const [hasPaid, setHasPaid] = useState(false)
    const [showKeyInput, setShowKeyInput] = useState(false)
    const [invalidKey, setInvalidKey] = useState(false)
    const [buzzAnimation, setBuzzAnimation] = useState(0)
    const inputRef = useRef<HTMLInputElement>()
    const location = useLocation()
    const intl = useIntl()

    /**
     * Hide dialog to register if a valid session is detected.
     */
    useEffect(() => {
        const sessionId = localStorage.getItem("sessionId")
        if (hasPaid || !sessionId) return
        validateKey(undefined, sessionId).then(response => {
            if (response.creatorKey) setHasPaid(true)
        })
    }, [hasPaid, open])

    const registerKey = async () => {
        const inputValue = inputRef.current?.value?.trim()
        if (!inputValue) return
        try {
            const response = await validateKey(inputValue, undefined)
            if (!response.creatorKey) {
                setInvalidKey(true)  // Buzz 🐝
                console.warn(`Invalid key: ${inputValue}`)
                Sentry.captureMessage("Invalid key attempt", { level: "error", extra: {
                    key: inputValue,
                    sessionId: localStorage.getItem("sessionId")
                }})
                setBuzzAnimation(prev => prev + 1)
                inputRef.current?.blur()
                return
            } else {
                const keyDetails = JSON.parse(response.creatorKey)
                localStorage.setItem("sessionId", keyDetails.sessionId)
                setInvalidKey(false)
                setShowKeyInput(false)
                setHasPaid(true)
                setCheckoutSuccess(true)
            }
        } catch (e: any) {
            console.error("Error registering key:", e.message)
            Sentry.captureException(e, { extra: { context: "Error registering key" }})
        }
    }

    /**
     * Navigate to the Stripe backend.
     */
    const handleCheckout = async () => {
        try {
            console.log("Proceeding to Checkout...")
            Sentry.captureMessage("🛒 Proceeding to checkout...", {level: "log", extra: { locale: intl.locale }})
            const { session } = await createCheckoutSession(location.pathname, intl.locale)
            console.log(session.url)
            if (session) window.location.href = session.url  // go to Stripe
        } catch (e) {
            console.error("Checkout failed", e)
        }
    }

    return (
        <Modal id="pricing-services" closeIcon open={open} onClose={onClose} size="large" dimmer="blurring" transition={{ animation: "fade", duration: 1 }}>

            <Modal.Header style={{ marginTop: "30px" }}><FormattedMessage id="pricing.header"/></Modal.Header>
            <Modal.Content>
                <Grid stackable columns={3}>

                    {/* COMMUNITY */}
                    <Grid.Column className="pricing-column">
                        <p className="pricing-title"><FormattedMessage id="pricing.community.title"/></p>
                        <p className="pricing-role"><FormattedMessage id="pricing.community.role"/></p>
                        <div className="pricing-fee">
                            <p><FormattedMessage id="pricing.community.price"/></p>
                        </div>
                        {screenSize === ScreenSize.LARGE && <Button className="button-spacer"/>}
                        <ul>
                            <li><FormattedMessage id="pricing.community.viewer"/></li>
                            <li><FormattedMessage id="pricing.community.creator" values={{
                                trial: <span style={{ color: "var(--creator-hint-color)", fontWeight: "bold" }}>
                                    {Number(process.env.REACT_APP_CREATOR_TRIAL)}
                                </span>
                            }}/></li>
                        </ul>
                    </Grid.Column>

                    {/* CREATOR */}
                    <Grid.Column className="pricing-column">
                        <p className="pricing-title"><FormattedMessage id="pricing.creator.title"/></p>
                        <p className="pricing-role"><FormattedMessage id="pricing.creator.role"/></p>
                        <div className="pricing-fee">
                            <p><FormattedMessage id="pricing.creator.price"/></p>
                            <p><FormattedMessage id="pricing.creator.price.once"/></p>
                        </div>
                        {/* CHECKOUT | REGISTER */}
                        {!hasPaid && !showKeyInput && <Button id="checkout" primary onClick={handleCheckout}>
                            <FormattedMessage id="pricing.button.checkout"/>
                        </Button>}
                        {/* BACK | REGISTER | INPUT */}
                        {showKeyInput && <Icon className="pricing-back" size="large" name="backward" onClick={() => {
                            setShowKeyInput(false)
                            setInvalidKey(false)
                        }}/>}
                        {!hasPaid && <Button id="paste-key" onClick={async() => {
                            if (!showKeyInput) {
                                setShowKeyInput(true)
                                setTimeout(() => { inputRef.current?.focus() }, 0)
                                return
                            }
                            await registerKey()
                        }}>
                            <FormattedMessage id={showKeyInput ? "pricing.button.key.save" : "pricing.button.key.paste"} />
                        </Button>}
                        {showKeyInput && <div id="key-input">
                            <span>🔑</span>
                            <Input
                                key={buzzAnimation}
                                size="small"
                                maxLength={14}
                                input={{ ref: inputRef, className: invalidKey ? "invalid-key" : "" }}
                                onKeyDown={async (e: { key: string }) => { if (e.key === "Enter") await registerKey() }}
                            />
                        </div>}
                        {/* ALREADY HAS KEY */}
                        {hasPaid && <Button id="existing-key" onClick={onClose}>
                            {checkoutSuccess
                                ? <FormattedMessage id="payment.success.active"/>    // Payment just confirmed
                                : <FormattedMessage id="pricing.button.key.close"/>  // Already had a key
                            } &nbsp;🎉
                        </Button>}
                        <ul>
                            <li><FormattedMessage id="pricing.creator.features"/></li>
                            <li><FormattedMessage id="pricing.creator.excel"/></li>
                            <li><FormattedMessage id="pricing.creator.academia"/></li>
                        </ul>
                    </Grid.Column>

                    {/* PROFESSIONAL */}
                    <Grid.Column className="pricing-column">
                        <p className="pricing-title"><FormattedMessage id="pricing.professional.title"/></p>
                        <p className="pricing-role">
                            <FormattedMessage id="pricing.professional.role"/>
                        </p>
                        <div className="pricing-fee">
                            <p><FormattedMessage id="pricing.professional.price"/></p>
                            <p><FormattedMessage id="pricing.professional.price.quotes"/></p>
                        </div>
                        <Button primary onClick={() => {
                            setContactModalOpen(true)
                            onClose()  // Close underlying modal
                        }}>Contact us</Button>
                        <ul>
                            <li><FormattedMessage id="pricing.professional.assistance"/></li>
                            <li><FormattedMessage id="pricing.professional.interviews"/></li>
                            <li><FormattedMessage id="pricing.professional.analytics"/></li>
                        </ul>
                    </Grid.Column>
                </Grid>
            </Modal.Content>
        </Modal>
    )
}